// Free tier — the money gate.
//
// Every money button asks ONE question: can this talent take money yet
// (utils/takesMoney)? If not, the button opens one of two sheets:
//
//   bank     no bank connected        → "Connect your bank to receive paid requests"
//                                        (MassPay countries: "Get paid in Indonesia with MassPay")
//   review   bank done, not approved  → Instagram / "You're in review" / declined
//
// Any screen calls openMoneyGate(profile). The two sheets are mounted once,
// for every signed-in talent page (RouteGuards.RequireAuth). The profile rides
// along so the bank sheet opens on the right face (Stripe or MassPay) at once.
import type { PayoutInfo } from "../types/payout";

export type MoneyGate = "bank" | "review";
export type GateProfile = {
  approvalStatus?: string;
  isStripeOnboarded?: boolean;
  isAdminCreated?: boolean;
  chosenDeferredStripe?: boolean;
  payout?: PayoutInfo | null;
} | null | undefined;
type Listener = (gate: MoneyGate | null, profile?: GateProfile) => void;

const listeners = new Set<Listener>();
const emit = (gate: MoneyGate | null, profile?: GateProfile) => listeners.forEach((l) => l(gate, profile));

// MassPay's own payee holds: payouts stop until support sorts it out.
const MASSPAY_HOLD = new Set(["LOCKED", "DEACTIVE", "CLOSED"]);

export const masspayOnHold = (status?: string | null): boolean => MASSPAY_HOLD.has(String(status || "").toUpperCase());

/** MassPay's ID check passed and the payee is not on hold: the MassPay twin of isStripeOnboarded. */
export function masspayVerified(profile: GateProfile): boolean {
  const m = profile?.payout?.masspay;
  return m?.kycStatus === "approved" && !masspayOnHold(m.status);
}

/** Is the talent's payout side done (Stripe, MassPay, or an account that has none by design)? */
export function bankDone(profile: GateProfile): boolean {
  return !!profile?.isStripeOnboarded || !!profile?.isAdminCreated || !!profile?.chosenDeferredStripe || masspayVerified(profile);
}

/** Which sheet is this talent's next step? A declined applicant is never offered the bank. */
export function whichGate(profile: GateProfile): MoneyGate {
  if (profile?.approvalStatus === "declined") return "review";
  return bankDone(profile) ? "review" : "bank";
}

/** Is Instagram the one thing left? The bank is done and there's nothing to review yet. */
export function instagramIsNext(profile: GateProfile): boolean {
  return profile?.approvalStatus === "needs_instagram" && bankDone(profile);
}

export const openMoneyGate = (profile: GateProfile): void => emit(whichGate(profile), profile);
export const openConnectBank = (profile?: GateProfile): void => emit("bank", profile);
/** The Instagram / review sheet — the "Connect Instagram" email link (?verify=1). */
export const openReviewSheet = (): void => emit("review");
export const closeMoneyGate = (): void => emit(null);

export function onMoneyGate(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
